import {
	FileOutlined,
	FilePdfOutlined,
	FileImageOutlined,
	PlayCircleOutlined,
	FileTextOutlined,
	AudioOutlined
} from "@ant-design/icons";
import { RcFile } from "antd/es/upload";
import { storage } from "config/firebase/firebase";
import { MediaType } from "constants/field-values";
import { v4 as uuidv4 } from "uuid";
import {
	ref,
	uploadBytesResumable,
	uploadBytes,
	getDownloadURL,
	deleteObject,
	getStorage,
	type StorageReference
} from "firebase/storage";
import { Upload, message as antMessage } from "antd";
import type { GetProp, UploadFile, UploadProps } from "antd";
import _differenceBy from "lodash/differenceBy";
import { heicTo } from "heic-to";

// Native browser support lists (ReactPlayer uses browser's <audio>/<video>).
// These are the single source of truth — SUPPORTED_MIME_TYPES is derived from them.
const SUPPORTED_IMAGE_TYPES = new Set([
	"image/jpeg",
	"image/jpg",
	"image/png",
	"image/gif",
	"image/webp",
	"image/avif",
	"image/svg+xml"
]);
const SUPPORTED_AUDIO_TYPES = new Set([
	"audio/mpeg",
	"audio/mp3",
	"audio/wav",
	"audio/x-wav",
	"audio/wave",
	"audio/ogg",
	"audio/vorbis",
	"audio/aac",
	"audio/x-aac",
	"audio/flac"
]);
const SUPPORTED_VIDEO_TYPES = new Set(["video/mp4", "video/webm", "video/ogg"]);

/** Additional document MIME types beyond image/audio/video. */
const SUPPORTED_DOCUMENT_TYPES = new Set([
	"application/pdf",
	"text/plain",
	"text/markdown",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document"
]);

/**
 * Full set of MIME types supported by the platform.
 * Derived as the union of the four category sets above so there is
 * a single place to add/remove types without risking drift.
 */
export const SUPPORTED_MIME_TYPES: Set<string> = new Set([
	...SUPPORTED_IMAGE_TYPES,
	...SUPPORTED_AUDIO_TYPES,
	...SUPPORTED_VIDEO_TYPES,
	...SUPPORTED_DOCUMENT_TYPES
]);

/**
 * Checks if a file is of a type supported by the platform
 */
export const isSupportedFileType = (mimeType: string): boolean => {
	return SUPPORTED_MIME_TYPES.has(mimeType);
};

export function needsConversion(mimeType: string): boolean {
	if (mimeType.startsWith("image/")) return !SUPPORTED_IMAGE_TYPES.has(mimeType);
	if (mimeType.startsWith("audio/")) return !SUPPORTED_AUDIO_TYPES.has(mimeType);
	if (mimeType.startsWith("video/")) return !SUPPORTED_VIDEO_TYPES.has(mimeType);
	return false;
}

/**
 * Get file extension from filename
 */
export const getFileExtension = (fileNameorPath: string): string => {
	const dividedName = fileNameorPath.split(".");
	const fileExtension = dividedName.length > 1 ? dividedName[dividedName.length - 1] : "";

	return fileExtension;
};

interface FileInfo {
	fileType: string; // fileType (photo, document, video, etc)
	extension: string; // file extension (jpg, pdf, mp4, etc)
	fileName: string; // full file Name
}

export const getFirebaseFileInfo = (url: string): FileInfo => {
	try {
		// Decode the URL to handle special characters
		const decodedUrl = decodeURIComponent(url);

		let filePath = "";

		if (decodedUrl.startsWith("gs://")) {
			// Extracts the file path from a URL gs:// (everything after the bucket name)
			const gsMatch = decodedUrl.match(/^gs:\/\/[^/]+\/(.+)$/);
			if (gsMatch) {
				filePath = gsMatch[1];
			}
		} else {
			// Extracts the file path from a Firebase Storage HTTPS URL.
			// Captures everything after "/o/" up to a "?" or the end of the string
			const pathMatch = decodedUrl.match(/\/o\/([^?]+)/);
			if (pathMatch) {
				filePath = pathMatch[1];
			}
		}

		if (!filePath) {
			throw new Error("Invalid Firebase or Google Cloud Storage URL");
		}

		const fileName = filePath.split("/").pop() || "";

		// Determines the file type based on the file path
		let fileType = "unknown";
		if (
			filePath.includes("property-photos") ||
			filePath.includes("profile-photos") ||
			filePath.includes("organization-photos") ||
			filePath.includes("property-documents/photos") ||
			filePath.includes("products-attachments/photos") ||
			filePath.includes("device-detection-files/photos")
		) {
			fileType = "image";
		} else if (
			filePath.includes("property-documents/documents") ||
			filePath.includes("products-attachments/documents") ||
			filePath.includes("device-detection-files/documents")
		) {
			fileType = "document";
		} else if (
			filePath.includes("property-documents/videos") ||
			filePath.includes("products-attachments/videos") ||
			filePath.includes("device-detection-files/videos")
		) {
			fileType = "video";
		} else if (filePath.includes("property-documents/audios") || filePath.includes("products-attachments/audios")) {
			fileType = "audio";
		}

		// Extracts the file extension
		const extensionMatch = fileName.match(/\.([^.]+)$/);
		const extension = extensionMatch ? extensionMatch[1].toLowerCase() : "";

		return {
			fileType,
			extension,
			fileName
		};
	} catch (error) {
		console.error("Error parsing Storage URL:", error);
		return {
			fileType: "unknown",
			extension: "",
			fileName: ""
		};
	}
};

export const sanitizeFileName = (fileName: string): string => {
	// Remove the file extension to process only the name part
	const nameWithoutExt = fileName.replace(/\.[^/.]+$/, "");

	// Extract the original file extension
	const extension = fileName.split(".").pop();

	// Apply aggressive sanitization to the name only
	const sanitized = nameWithoutExt
		.toLowerCase() // Convert to lowercase for consistency
		.replace(/[^a-z0-9]/g, "-") // Replace ANY non-alphanumeric character with hyphen
		.replace(/-+/g, "-") // Collapse multiple consecutive hyphens into single hyphen
		.replace(/^-+|-+$/g, ""); // Remove leading and trailing hyphens

	// Reconstruct the filename with extension (if it exists)
	return extension ? `${sanitized}.${extension}` : sanitized;
};

export const getFileTypeFromMimeType = (mimeType: string) => {
	switch (true) {
		case mimeType.startsWith("image/"):
			return MediaType.Image;
		case mimeType.startsWith("video/"):
			return MediaType.Video;
		case mimeType.startsWith("audio/"):
			return MediaType.Audio;
		case mimeType.startsWith("application/pdf"):
			return MediaType.PDF;
		default:
			return MediaType.Document;
	}
};

export const getFileIcon = (mimeType: string) => {
	switch (true) {
		case mimeType.startsWith("image/"):
			return FileImageOutlined;
		case mimeType.startsWith("video/"):
			return PlayCircleOutlined;
		case mimeType.startsWith("audio/"):
			return AudioOutlined;
		case mimeType.startsWith("application/pdf"):
			return FilePdfOutlined;
		case mimeType.includes("text"):
		case mimeType.includes("document"):
			return FileTextOutlined;
		default:
			return FileOutlined;
	}
};

// Helper function to extract filename from file_uri
export const getFileNameFromUri = (fileUri: string): string => {
	try {
		// Handle Firebase Storage HTTPS URLs
		if (fileUri.includes("firebasestorage.googleapis.com")) {
			// Extract path from URL (between /o/ and ?)
			const match = fileUri.match(/\/o\/(.+?)\?/);
			if (match) {
				// Decode URL encoding (%2F, %20, etc.)
				const encodedPath = match[1];
				const decodedPath = decodeURIComponent(encodedPath);
				// Get filename from path
				const parts = decodedPath.split("/");
				const fileName = parts[parts.length - 1];
				// Remove prefix pattern: {type}-{uuid}-{timestamp}-
				const cleanName = fileName.replace(/^(photo|video|document|audio)-[a-f0-9-]+-\d+-/, "");
				return cleanName;
			}
		}

		// Handle gs:// URIs (legacy/fallback)
		if (fileUri.startsWith("gs://")) {
			const parts = fileUri.split("/");
			const fileName = parts[parts.length - 1];
			const cleanName = fileName.replace(/^(photo|video|document|audio)-[a-f0-9-]+-\d+-/, "");
			return cleanName;
		}

		return "file";
	} catch {
		return "file";
	}
};

export const createFirebaseFileName = (file: File, filePrefix: string, name: string) => {
	const uniqueID = uuidv4();
	const timestamp = Math.floor(Date.now() / 1000);
	const fileExtension = getFileExtension(file.name);
	const fileId = name ? `${name}.${fileExtension}` : file.name;
	const fileName = `${filePrefix}-${uniqueID}-${timestamp}-${fileId}`;
	return fileName;
};

// Real Firebase upload function
export async function uploadToStorage(
	file: File,
	onProgress: (progress: number) => void,
	filePath?: string
): Promise<{
	filePath: string;
	bucketName: string;
	publicUrl: string;
}> {
	return new Promise((resolve, reject) => {
		try {
			const timestamp = Date.now();
			const finalFilePath = filePath || `uploads/${timestamp}-${file.name}`;
			const storageRef = ref(storage, finalFilePath);

			const uploadTask = uploadBytesResumable(storageRef, file);

			uploadTask.on(
				"state_changed",
				snapshot => {
					// Progress callback
					const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
					onProgress(Math.round(progress));
				},
				error => {
					// Error callback
					console.error("Upload failed:", error);
					reject(error);
				},
				async () => {
					// Success callback
					try {
						const publicUrl = await getDownloadURL(uploadTask.snapshot.ref);
						const bucketName = JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG || "{}").storageBucket;

						resolve({
							filePath: finalFilePath,
							bucketName,
							publicUrl
						});
					} catch (error) {
						reject(error);
					}
				}
			);
		} catch (error) {
			reject(error);
		}
	});
}

// Delete file from Firebase storage
export async function deleteFromStorage(fileUrl: string, firebaseRelativePath?: string): Promise<void> {
	try {
		let filePath = firebaseRelativePath;

		if (!filePath) {
			// Extract the file path from the URL
			// Firebase storage URLs have format: https://firebasestorage.googleapis.com/v0/b/bucket-name/o/path%2Fto%2Ffile?alt=media&token=...
			const url = new URL(fileUrl);
			const pathMatch = url.pathname.match(/\/o\/(.+)$/);

			if (!pathMatch) {
				throw new Error("Invalid Firebase storage URL format");
			}

			// Decode the file path (Firebase encodes / as %2F)
			filePath = decodeURIComponent(pathMatch[1]);
		}

		const fileRef = ref(storage, filePath);
		await deleteObject(fileRef);

		console.log("Successfully deleted file from storage:", filePath);
	} catch (error) {
		console.error("Error deleting file from storage:", error);
		throw error;
	}
}

export type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

export const getBase64 = (file: FileType | RcFile): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = error => reject(error);
	});

export const readAsDataUrl = (file: File): Promise<string> =>
	new Promise(resolve => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result as string);
	});

export async function convertHeicFileToJpeg(file: File): Promise<File> {
	const blob = await heicTo({ blob: file, type: "image/jpeg", quality: 0.9 });
	return new File([blob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" });
}

export type FeedbackAttachment = {
	uid: string;
	type: string;
	storageRef: StorageReference;
	image_link?: string;
	video_link?: string;
};

export function deleteFeedbackAttachments(attachments: FeedbackAttachment[]): void {
	attachments.forEach(async attachment => {
		try {
			await deleteObject(attachment.storageRef);
		} catch (error) {
			console.error("Error deleting uploaded file from discarded review:", error);
		}
	});
}

export async function uploadFeedbackAttachments({
	fileList,
	uploadedAttachments,
	bucketName,
	fileNamePrefix,
	setUploadedAttachments
}: {
	fileList: UploadFile[];
	uploadedAttachments: FeedbackAttachment[];
	bucketName: string;
	fileNamePrefix: string;
	setUploadedAttachments: (attachments: FeedbackAttachment[]) => void;
}): Promise<FeedbackAttachment[] | undefined> {
	const submittableFiles = fileList.filter(file => file.status === "done");
	if (submittableFiles.length === 0) return [];

	const remainingAttachments: FeedbackAttachment[] = [];
	const removedAttachments: FeedbackAttachment[] = [];

	uploadedAttachments.forEach(attachment => {
		if (submittableFiles.find(file => file.uid === attachment.uid)) {
			remainingAttachments.push(attachment);
		} else {
			removedAttachments.push(attachment);
		}
	});

	await Promise.allSettled(removedAttachments.map(({ storageRef }) => deleteObject(storageRef))).then(res => {
		res.forEach((result, index) => {
			if (result.status === "rejected") {
				console.error(`Error deleting previously uploaded file: ${removedAttachments[index].uid}`, result.reason);
			}
		});
	});

	let uploads: FeedbackAttachment[] = [];

	try {
		const newFiles = _differenceBy(submittableFiles, uploadedAttachments, "uid");
		const uniqueID = uuidv4();
		const fbStorage = getStorage();

		for (let i = 0; i < newFiles.length; i++) {
			const file = newFiles[i];
			const fileName = `${fileNamePrefix}-${uniqueID}-${Math.floor(Date.now() / 1000)}-${file.name}`;
			const fileType = file.type || "";
			let subFolder: string;

			if (fileType.startsWith("video")) {
				subFolder = "videos";
			} else if (fileType.startsWith("image")) {
				subFolder = "images";
			} else {
				continue;
			}

			const storageRef = ref(fbStorage, `${bucketName}/${subFolder}/${fileName}`);
			await uploadBytes(storageRef, file.originFileObj as File);
			uploads.push({ uid: file.uid, type: fileType, storageRef });
		}

		if (uploads.length === 0) {
			if (remainingAttachments.length) {
				setUploadedAttachments(remainingAttachments);
			}
			return [];
		}

		const urls = await Promise.all(uploads.map(({ storageRef }) => getDownloadURL(storageRef)));
		const newAttachments = uploads.map((u, index) => ({
			[u.type.startsWith("video/") ? "video_link" : "photo_link"]: urls[index],
			...u
		})) as FeedbackAttachment[];
		setUploadedAttachments([...remainingAttachments, ...newAttachments]);
		return newAttachments;
	} catch (error) {
		console.error("Error uploading files:", error);
		alert("There was an error uploading your attachments. Please try again later.");

		await Promise.allSettled(uploads.map(({ storageRef }) => deleteObject(storageRef))).then(res => {
			res.forEach((result, index) => {
				if (result.status === "rejected") {
					console.error(`Error deleting failed attachment: ${uploads[index].uid}`, result.reason);
				}
			});
		});

		return undefined;
	}
}

// ─── beforeUpload factory ────────────────────────────────────────────────────

/**
 * Factory that returns a ready-to-use `beforeUpload` handler for Ant Design's
 * `<Upload>` component. It handles two concerns in order:
 *
 * 1. **HEIC/HEIF conversion** — automatically converts the file to JPEG before
 *    Ant Design processes it, showing an error toast on failure.
 * 2. **Type validation (optional)** — if `allowedTypePattern` is provided, files
 *    whose MIME type does not match are rejected with `Upload.LIST_IGNORE`.
 *
 * @example
 * // HEIC conversion only (e.g. add-attachment-modal)
 * beforeUpload={createBeforeUpload()}
 *
 * @example
 * // HEIC conversion + restrict to images and videos
 * beforeUpload={createBeforeUpload({ allowedTypePattern: /^image\/|^video\// })}
 */
export function createBeforeUpload(options?: {
	/** RegExp that must match `file.type`. Files that do not match are ignored. */
	allowedTypePattern?: RegExp;
	/** Called when the file type is rejected. Defaults to `message.error`. */
	onInvalidType?: (file: File) => void;
}): UploadProps["beforeUpload"] {
	return async file => {
		// Step 1: Convert HEIC/HEIF → JPEG
		const isHeicFile = /\.(heic|heif)$/i.test(file.name) || file.type === "image/heic" || file.type === "image/heif";

		if (isHeicFile) {
			try {
				return await convertHeicFileToJpeg(file);
			} catch {
				antMessage.error("Failed to convert HEIC image.");
				return Upload.LIST_IGNORE;
			}
		}

		// Step 2: Optional type validation
		if (options?.allowedTypePattern && !options.allowedTypePattern.test(file.type)) {
			if (options.onInvalidType) {
				options.onInvalidType(file);
			} else {
				antMessage.error("File type not allowed.");
			}
			return Upload.LIST_IGNORE;
		}
	};
}
